import React, { useState } from 'react';
import {
    Alert,
    Box,
    InputAdornment,
    Link,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { formatDisplay } from 'helpers/formatHelper';
import { useTranslation } from 'react-i18next';
import { PreviewData } from 'routes/Cellar/types';
import Flex from 'ui/Flex/Flex';
import SwitchController from './SwitchController';

const TABLE_NAME_MAX_LENGTH = 63;

const GeoPortail = (
    <Link
        sx={{
            color: 'inherit',
            textDecoration: 'underline',
        }}
        href="https://www.geoportail.gouv.fr/"
    >
        Geoportail
    </Link>
);

type PreVizHeaderProps = {
    isNewTable: boolean;
    malformattedPrevizData: PreviewData;
    newTableName: string;
    previzData: PreviewData;
    setNewTableName: (arg: string) => void;
    setShowMalformatted: (arg: boolean) => void;
    showMalformatted: boolean;
    showGeoTypeAlert: boolean;
};

const PreVizHeader = ({
    isNewTable,
    malformattedPrevizData,
    newTableName,
    previzData,
    setNewTableName,
    setShowMalformatted,
    showMalformatted,
    showGeoTypeAlert,
}: PreVizHeaderProps) => {
    const { t } = useTranslation();
    const [isTableNameMaxLengthReached, setIsTableNameMaxLengthReached] =
        useState(false);
    const previewLengthTooltipTitle = (
        <Box sx={{ textAlign: 'justify' }}>
            {t('importCSV.previewLengthExplain')}
        </Box>
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setIsTableNameMaxLengthReached(
            newValue.length >= TABLE_NAME_MAX_LENGTH,
        );
        setNewTableName(newValue.slice(0, TABLE_NAME_MAX_LENGTH));
    };

    return (
        <Flex flexDirection="column">
            {isNewTable && (
                <TextField
                    color={isTableNameMaxLengthReached ? 'warning' : undefined}
                    helperText={
                        isTableNameMaxLengthReached
                            ? t('importCSV.newTableMaxLengthMessage', {
                                  max_length: TABLE_NAME_MAX_LENGTH,
                              })
                            : undefined
                    }
                    onChange={handleChange}
                    placeholder={t('importCSV.newTablePlaceholder')}
                    sx={{ width: '33%' }}
                    value={newTableName}
                    variant="standard"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <EditIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            )}
            <Flex
                align="flex-end"
                direction="column"
                sx={{ flexGrow: 1, pr: '1.5rem' }}
            >
                <Flex>
                    <Tooltip title={previewLengthTooltipTitle}>
                        <InfoIcon sx={{ mr: '0.5rem' }} fontSize="small" />
                    </Tooltip>
                    <Typography variant="body2">
                        {t('importCSV.previewLength')}:{' '}
                        {formatDisplay(
                            showMalformatted
                                ? malformattedPrevizData.nbRows
                                : previzData.nbRows,
                            'integer',
                        )}
                        /{formatDisplay(previzData.fileLength, 'integer')}
                    </Typography>
                </Flex>
                <SwitchController
                    checked={showMalformatted}
                    label={
                        <Typography variant="body2">
                            {t('importCSV.showMalformattedRows')}
                        </Typography>
                    }
                    name="malformatted-data"
                    onChange={() => setShowMalformatted(!showMalformatted)}
                />
            </Flex>
            {showGeoTypeAlert && (
                <Alert severity="info" sx={{ textAlign: 'center' }}>
                    {t('importCSV.geoTypeAlert')}
                    {GeoPortail}
                </Alert>
            )}
        </Flex>
    );
};

export default React.memo(PreVizHeader);
